import React, { useCallback, useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "../ui/sonner";
import { chunkUpload } from "../EnginePanel";
import { evidenceUrl } from "../EvidenceLightbox";
import { VrViewer } from "./VrViewer";
import { buildVrEditPatch } from "../../lib/vrLinks";
import { Copy, Globe, Link2, Loader2, Orbit, Pencil, Play, QrCode, RefreshCw, Trash2, Upload } from "lucide-react";

const fmt = (iso) => { if (!iso) return ""; const d = new Date(iso); const p = (n) => String(n).padStart(2, "0"); return `${p(d.getMonth() + 1)}/${p(d.getDate())}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`; };

const VrThumb = ({ it, zh, onOpen, onDel, onEdit, canDel, canEdit }) => (
  <div className="relative group rounded-lg overflow-hidden border border-slate-200 bg-slate-900 aspect-[2/1]" data-testid={`vr-item-${it.id}`}>
    <button onClick={onOpen} className="absolute inset-0 w-full h-full text-left" data-testid={`vr-item-open-${it.id}`} title={zh ? "查看全景" : "View"}>
      {it.kind === "photo" && <img src={evidenceUrl(it.fileId)} alt={it.filename || "360"} loading="lazy" className="h-full w-full object-cover opacity-90 group-hover:opacity-100 transition-opacity" />}
      {it.kind !== "photo" && (
        <div className="h-full w-full flex flex-col items-center justify-center text-white/80 gap-1">
          {it.kind === "video" ? <Play className="h-6 w-6" /> : <Globe className="h-6 w-6" />}
          <span className="text-[10px] px-2 truncate max-w-full">{it.kind === "video" ? (it.filename || "360 Video") : (it.title || it.url)}</span>
        </div>
      )}
      <span className="absolute left-1.5 top-1.5 text-[9px] px-1.5 py-0.5 rounded-full bg-black/60 text-white">{it.kind === "photo" ? "360°" : it.kind === "video" ? (zh ? "全景视频" : "360 Video") : (zh ? "云端链接" : "Cloud")}</span>
      <span className="absolute left-1.5 bottom-1.5 text-[9px] text-white/80">{it.by} · {fmt(it.at)}</span>
      {(it.markers || []).length > 0 && <span className="absolute right-1.5 bottom-1.5 text-[9px] px-1.5 py-0.5 rounded-full bg-brand-500 text-white" data-testid={`vr-item-markers-${it.id}`}>{it.markers.length} {zh ? "标注" : "marks"}</span>}
    </button>
    {canEdit && <button onClick={onEdit} className="absolute right-8 top-1.5 p-1 rounded bg-black/60 text-white/80 hover:bg-cyan-700 hover:text-white" data-testid={`vr-item-edit-${it.id}`} title={zh ? "编辑" : "Edit"}><Pencil className="h-3 w-3" /></button>}
    {canDel && <button onClick={onDel} className="absolute right-1.5 top-1.5 p-1 rounded bg-black/60 text-white/80 hover:bg-red-600 hover:text-white" data-testid={`vr-item-del-${it.id}`} title={zh ? "删除" : "Delete"}><Trash2 className="h-3 w-3" /></button>}
  </div>
);

/* 现场踏勘采集 → 360 VR 面板：上传全景照片/视频或粘贴云端链接 · 直接全景查看 · 免登录分享链接 + 二维码（分包报价资料包自动附带） */
export const VrPanel = ({ api, pid, task, zh, canManage, canMark }) => {
  const T = (a, b) => (zh ? a : b);
  const [data, setData] = useState({ items: [], shareUrl: null });
  const [busy, setBusy] = useState(false);
  const [pct, setPct] = useState(null);
  const [link, setLink] = useState("");
  const [showLink, setShowLink] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [view, setView] = useState(null);
  // 就地编辑一条（标题 / 链接）：链接类可改地址，上传件只能改标题
  const [editId, setEditId] = useState("");
  const [editTitle, setEditTitle] = useState("");
  const [editUrl, setEditUrl] = useState("");
  const fileRef = useRef(null);
  const load = useCallback(() => api.get(`/projects/${pid}/engine/vr`).then(({ data: d }) => setData(d)).catch(() => {}), [api, pid]);
  useEffect(() => { load(); }, [load]);

  const upload = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setBusy(true);
    try {
      for (const f of list) {
        setPct(0);
        const up = await chunkUpload(api, f, setPct);
        await api.post(`/projects/${pid}/engine/tasks/${task.id}/vr`, up);
      }
      toast.success(T(`已上传 ${list.length} 个 360 VR 文件`, `${list.length} file(s) uploaded`));
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || T("上传失败", "Upload failed")); }
    finally { setBusy(false); setPct(null); if (fileRef.current) fileRef.current.value = ""; }
  };
  const addLink = async () => {
    const url = link.trim();
    if (!url) return;
    setBusy(true);
    try { await api.post(`/projects/${pid}/engine/tasks/${task.id}/vr`, { url }); setLink(""); setShowLink(false); toast.success(T("云端链接已添加", "Link added")); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || T("添加失败", "Failed")); }
    finally { setBusy(false); }
  };
  const del = async (it) => {
    if (!window.confirm(T("删除该 360 VR？", "Delete this item?"))) return;
    try { await api.delete(`/projects/${pid}/engine/vr/${it.id}`); toast.success(T("已删除", "Deleted")); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || T("删除失败", "Failed")); }
  };
  const startEdit = (it) => { setEditId(it.id); setEditTitle(it?.title || ""); setEditUrl(it?.url || ""); };
  const cancelEdit = () => { setEditId(""); setEditTitle(""); setEditUrl(""); };
  const saveEdit = async (it) => {
    const { patch, error } = buildVrEditPatch(it, { title: editTitle, url: editUrl });
    if (error) { toast.error(T("链接不能为空", "Link required")); return; }
    try {
      await api.patch(`/projects/${pid}/engine/vr/${it.id}`, patch);
      toast.success(T("已保存", "Saved"));
      cancelEdit();
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || T("保存失败", "Failed to save")); }
  };
  const share = async (regenerate) => {
    try {
      const { data: d } = await api.post(`/projects/${pid}/engine/vr/share`, { regenerate: !!regenerate });
      setData((x) => ({ ...x, shareUrl: d.url }));
      if (regenerate) toast.success(T("已重新生成链接，旧链接失效", "Link regenerated"));
      return d.url;
    } catch (e) { toast.error(e?.response?.data?.detail || T("生成链接失败", "Failed")); return null; }
  };
  const addMarker = async (it, m) => {
    try {
      const { data: mk } = await api.post(`/projects/${pid}/engine/vr/${it.id}/markers`, m);
      const upd = (x) => (x.id === it.id ? { ...x, markers: [...(x.markers || []), mk] } : x);
      setData((d) => ({ ...d, items: d.items.map(upd) }));
      setView((v) => (v && v.id === it.id ? upd(v) : v));
      toast.success(T("标注已保存", "Marker saved"));
      return true;
    } catch (e) { toast.error(e?.response?.data?.detail || T("标注失败", "Failed")); return false; }
  };
  const delMarker = async (it, m) => {
    try {
      await api.delete(`/projects/${pid}/engine/vr/${it.id}/markers/${m.id}`);
      const upd = (x) => (x.id === it.id ? { ...x, markers: (x.markers || []).filter((k) => k.id !== m.id) } : x);
      setData((d) => ({ ...d, items: d.items.map(upd) }));
      setView((v) => (v && v.id === it.id ? upd(v) : v));
    } catch (e) { toast.error(e?.response?.data?.detail || T("删除失败", "Failed")); }
  };
  const copy = async () => {
    const url = data.shareUrl || await share(false);
    if (!url) return;
    try { await navigator.clipboard.writeText(url); toast.success(T("分享链接已复制", "Link copied")); }
    catch (_) { setShowQr(true); toast.info(T("无法自动复制，请长按下方链接或扫二维码", "Clipboard unavailable — long-press the link or scan the QR")); }
  };

  return (
    <div className="rounded-lg border border-cyan-200 bg-cyan-50/40 p-2.5 space-y-2" data-testid="trace-ws-vr-panel">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[10px] font-bold uppercase tracking-wider text-cyan-800 inline-flex items-center gap-1"><Orbit className="h-3.5 w-3.5" />{T("360 VR 现场全景", "Site 360 VR")} <span className="text-cyan-600 font-normal" data-testid="trace-ws-vr-count">({data.items.length})</span></p>
        <div className="flex items-center gap-1">
          <button data-testid="trace-ws-vr-upload" disabled={busy} onClick={() => fileRef.current?.click()} className="text-[11px] px-2.5 py-1.5 rounded-lg bg-cyan-700 text-white hover:bg-cyan-800 inline-flex items-center gap-1 disabled:opacity-60">{busy && pct !== null ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}{pct !== null ? `${pct}%` : T("上传全景", "Upload")}</button>
          <button data-testid="trace-ws-vr-add-link" disabled={busy} onClick={() => setShowLink((v) => !v)} className="text-[11px] px-2.5 py-1.5 rounded-lg border border-cyan-300 text-cyan-800 hover:bg-cyan-100 inline-flex items-center gap-1"><Link2 className="h-3 w-3" />{T("云端链接", "Cloud link")}</button>
        </div>
      </div>
      <input ref={fileRef} type="file" multiple accept="image/*,video/mp4,video/quicktime,video/webm" className="hidden" data-testid="trace-ws-vr-file" onChange={(e) => upload(e.target.files)} />
      {showLink && (
        <div className="flex gap-1.5" data-testid="trace-ws-vr-link-form">
          <input value={link} onChange={(e) => setLink(e.target.value)} placeholder={T("粘贴 Insta360 / Kuula / Matterport 等云端全景链接 https://…", "Paste cloud 360 link https://…")} className="flex-1 text-[11px] border border-slate-300 rounded-lg px-2 py-1.5 bg-white" data-testid="trace-ws-vr-link-input" />
          <button onClick={addLink} disabled={busy || !link.trim()} className="text-[11px] px-2.5 py-1.5 rounded-lg bg-slate-900 text-white disabled:opacity-50" data-testid="trace-ws-vr-link-save">{T("添加", "Add")}</button>
        </div>
      )}
      {data.items.length === 0 ? (
        <p className="text-[10px] text-slate-500" data-testid="trace-ws-vr-empty">{T("尚无全景。上传 Insta360 / Theta 导出的等距全景 JPG 或全景 MP4，或粘贴云端链接；上传后可直接在此拖动查看、打点标注风险位置，并生成免登录分享链接；全景同时同步到踏勘清单 P1。", "No 360 content yet. Upload equirectangular JPG / MP4 or paste a cloud link.")}</p>
      ) : (
        <>
          {editId && (() => {
            const it = data.items.find((x) => x.id === editId);
            if (!it) return null;
            return (
              <div className="space-y-1.5 rounded-lg border border-cyan-200 bg-white p-2" data-testid={`trace-ws-vr-edit-form-${editId}`}>
                <input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} placeholder={T("标题（可空）", "Title (optional)")}
                  className="w-full text-[11px] border border-slate-300 rounded-lg px-2 py-1.5 bg-white" data-testid={`trace-ws-vr-edit-title-${editId}`} />
                <input value={editUrl} onChange={(e) => setEditUrl(e.target.value)} disabled={it.kind !== "link"} placeholder="https://..."
                  className="w-full text-[11px] border border-slate-300 rounded-lg px-2 py-1.5 bg-white disabled:bg-slate-50 disabled:text-slate-400" data-testid={`trace-ws-vr-edit-url-${editId}`} />
                <div className="flex justify-end gap-1.5">
                  <button onClick={cancelEdit} className="text-[11px] px-2.5 py-1.5 rounded-lg border border-slate-300 hover:bg-slate-50" data-testid={`trace-ws-vr-edit-cancel-${editId}`}>{T("取消", "Cancel")}</button>
                  <button onClick={() => saveEdit(it)} className="text-[11px] px-2.5 py-1.5 rounded-lg bg-cyan-700 text-white hover:bg-cyan-800" data-testid={`trace-ws-vr-edit-save-${editId}`}>{T("保存", "Save")}</button>
                </div>
              </div>
            );
          })()}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5" data-testid="trace-ws-vr-grid">
            {data.items.map((it) => <VrThumb key={it.id} it={it} zh={zh} canDel={canManage} canEdit={canManage}
              onEdit={() => startEdit(it)} onOpen={() => setView(it)} onDel={() => del(it)} />)}
          </div>
        </>
      )}
      {data.items.length > 0 && (
        <div className="rounded-lg bg-white border border-cyan-100 p-2 space-y-1.5" data-testid="trace-ws-vr-share">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[10px] font-semibold text-slate-600">{T("免登录分享链接", "Public link")}</span>
            <code className="text-[10px] text-cyan-800 bg-cyan-50 px-1.5 py-0.5 rounded truncate max-w-[220px]" data-testid="trace-ws-vr-share-url">{data.shareUrl || T("点击复制生成", "click copy to create")}</code>
            <button onClick={copy} className="text-[10px] px-2 py-1 rounded border border-slate-300 hover:bg-slate-50 inline-flex items-center gap-1" data-testid="trace-ws-vr-share-copy"><Copy className="h-3 w-3" />{T("复制", "Copy")}</button>
            <button onClick={async () => { if (!data.shareUrl) await share(false); setShowQr((v) => !v); }} className="text-[10px] px-2 py-1 rounded border border-slate-300 hover:bg-slate-50 inline-flex items-center gap-1" data-testid="trace-ws-vr-share-qr"><QrCode className="h-3 w-3" />{T("二维码", "QR")}</button>
            {canManage && <button onClick={() => window.confirm(T("重新生成后旧链接立即失效，确定？", "Old link will stop working. Continue?")) && share(true)} className="text-[10px] px-2 py-1 rounded border border-slate-300 hover:bg-slate-50 inline-flex items-center gap-1" data-testid="trace-ws-vr-share-regen"><RefreshCw className="h-3 w-3" />{T("重新生成", "Regenerate")}</button>}
          </div>
          <p className="text-[10px] text-slate-400">{T("业主 / 分包 / 设计部打开即可在浏览器查看云端 360 VR；分包报价邀请资料包已自动附带此链接。", "Owners / subs / designers can view in browser; auto-attached to RFQ packs.")}</p>
          {showQr && data.shareUrl && <div className="flex justify-center bg-white p-3 rounded-lg border border-slate-200" data-testid="trace-ws-vr-share-qr-svg"><QRCodeSVG value={data.shareUrl} size={160} level="M" /></div>}
        </div>
      )}
      <VrViewer item={view} src={view?.fileId ? evidenceUrl(view.fileId) : view?.url} zh={zh} onClose={() => setView(null)} canMark={!!canMark} onAddMarker={addMarker} onDeleteMarker={delMarker} />
    </div>
  );
};
